// components/MediaDisplay.jsx — Clean redesign
// - Removed mt/mb/border/borderRadius (parent FeedItem container owns all of that)
// - Subtle zoom hint replaces heavy black overlay
// - Fullscreen modal cleaned up
// - GIF animation preserved

import React, { useState } from "react";
import { Box, Skeleton, IconButton, Typography } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  ZoomIn   as ZoomInIcon,
  Close    as CloseIcon,
} from "@mui/icons-material";

export default function MediaDisplay({
  mediaUrl,
  mediaType,
  mediaWidth,
  mediaHeight,
  alt = "Media content",
  maxWidth = 600,
  maxHeight = 480,
}) {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError,  setHasError]  = useState(false);
  const [isZoomed,  setIsZoomed]  = useState(false);

  if (!mediaUrl || !mediaType) return null;

  /* ── Aspect-ratio-aware dimensions ──────────────────── */
  const getDimensions = () => {
    if (!mediaWidth || !mediaHeight) return { width: "100%", height: "auto" };
    const ratio = mediaWidth / mediaHeight;
    let w = mediaWidth, h = mediaHeight;
    if (w > maxWidth)  { w = maxWidth;  h = w / ratio; }
    if (h > maxHeight) { h = maxHeight; w = h * ratio; }
    return { width: w, height: h };
  };
  const { width, height } = getDimensions();

  /* ── Error state ─────────────────────────────────────── */
  if (hasError) {
    return (
      <Box sx={{
        py: 3, px: 2, textAlign: "center",
        color: "text.disabled",
        fontSize: "0.82rem",
        fontFamily: "'DM Sans', sans-serif",
      }}>
        Failed to load {mediaType === "gif" ? "GIF" : "image"}
      </Box>
    );
  }

  /* ── Main render ─────────────────────────────────────── */
  return (
    <>
      {/* Container — NO margin, border, or borderRadius here.
          FeedItem wraps this in its own styled Box. */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          cursor: "pointer",
          display: "block",
          lineHeight: 0,                  // prevent inline-block gap under img
          "&:hover .zoom-hint": { opacity: 1 },
        }}
        onClick={() => setIsZoomed(true)}
      >
        {/* Loading skeleton — matches intrinsic size */}
        {isLoading && (
          <Skeleton
            variant="rectangular"
            width="100%"
            height={typeof height === "number" ? height : 280}
            sx={{ display: "block" }}    // no borderRadius — parent clips it
          />
        )}

        {/* Image / GIF — use <img> to preserve GIF animation */}
        <img
          src={mediaUrl}
          alt={alt}
          onLoad={() => { setIsLoading(false); setHasError(false); }}
          onError={() => { setIsLoading(false); setHasError(true); }}
          style={{
            display: isLoading ? "none" : "block",
            width: "100%",
            height: "auto",
            maxWidth: typeof width === "number" ? width : "100%",
            // NO borderRadius — parent Box has overflow:hidden + borderRadius
            // NO border — parent Box owns the border
          }}
        />

        {/* Zoom hint — very subtle, not a full dark overlay */}
        {!isLoading && (
          <Box
            className="zoom-hint"
            sx={{
              position: "absolute",
              bottom: 8, right: 8,
              width: 28, height: 28,
              borderRadius: "6px",
              bgcolor: alpha(theme.palette.common.black, 0.42),
              display: "flex", alignItems: "center", justifyContent: "center",
              opacity: 0,
              transition: "opacity 0.15s ease",
              pointerEvents: "none",
            }}
          >
            <ZoomInIcon sx={{ color: "white", fontSize: 16 }} />
          </Box>
        )}
      </Box>

      {/* ── Fullscreen modal ─────────────────────────────── */}
      {isZoomed && (
        <Box
          sx={{
            position: "fixed", inset: 0,
            bgcolor: alpha(theme.palette.common.black, 0.88),
            zIndex: theme.zIndex.modal,
            display: "flex", alignItems: "center", justifyContent: "center",
            p: 2,
          }}
          onClick={() => setIsZoomed(false)}
        >
          {/* Close button */}
          <IconButton
            sx={{
              position: "absolute", top: 14, right: 14,
              color: "white", p: 0.75, borderRadius: "8px",
              bgcolor: alpha("#fff", 0.1),
              "&:hover": { bgcolor: alpha("#fff", 0.18) },
            }}
            onClick={() => setIsZoomed(false)}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>

          {/* Full-size image */}
          <img
            src={mediaUrl}
            alt={alt}
            style={{
              maxWidth: "95vw",
              maxHeight: "92vh",
              objectFit: "contain",
              borderRadius: "8px",
              display: "block",
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </Box>
      )}
    </>
  );
}